import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { MdClose, MdCheckCircle, MdError } from "react-icons/md";

import { dishImportActions } from "../../../store/slices/dishes-slices";
import css from "./ImportProgressChip.module.css";

/**
 * Floating dish-import status chip.
 *
 * Mounted in MainLayout rather than in the import modal so it survives route
 * changes — the admin can navigate anywhere while an import runs and still see
 * it. All state comes from the dishImport slice; this component is presentation
 * only and holds nothing locally.
 */
const ImportProgressChip = () => {
    const dispatch = useDispatch();
    const { phase, current, total, batch, batchCount, results } = useSelector(
        (store) => store.dishImport
    );

    if (phase === "idle") return null;

    const importing = phase === "importing";
    const pct = total ? Math.round((current / total) * 100) : 0;
    // Keep a sliver of bar while importing, otherwise the first batch renders at
    // 0% width and the shimmer has nothing to animate on.
    const barWidth = importing ? Math.max(pct, 5) : pct;
    const allFailed = results && results.successCount === 0;

    return (
        <div className={css.chip}>
            <div className={css.chipHeader}>
                {importing ? (
                    <div className={css.chipSpinner} />
                ) : allFailed ? (
                    <MdError className={css.chipIconError} />
                ) : (
                    <MdCheckCircle className={css.chipIconSuccess} />
                )}
                <span className={css.chipTitle}>
                    {importing ? "Importing dishes…" : "Import complete"}
                </span>
                {!importing && (
                    <button
                        className={css.chipClose}
                        onClick={() => dispatch(dishImportActions.dismiss())}
                    >
                        <MdClose />
                    </button>
                )}
            </div>

            <div className={css.chipBar}>
                <div
                    className={importing ? `${css.chipFill} ${css.chipFillActive}` : css.chipFill}
                    style={{ width: `${barWidth}%` }}
                />
            </div>

            {importing && (
                <span className={css.chipLabel}>
                    {current} / {total} dishes
                    {batchCount > 1 && ` · batch ${batch} of ${batchCount}`}
                </span>
            )}

            {!importing && results && (
                <>
                    <span className={css.chipLabel}>
                        {results.successCount} of {results.total} in {results.seconds}s
                    </span>
                    <span className={css.chipLabel}>
                        {results.createdCount} created · {results.updatedCount} updated
                        {results.skippedCount > 0 && ` · ${results.skippedCount} unchanged`}
                    </span>
                    {results.errors.length > 0 && (
                        <div className={css.chipErrors}>
                            <strong>{results.errors.length} failed:</strong>
                            <ul>
                                {results.errors.slice(0, 5).map((e, i) => (
                                    <li key={i}>{e}</li>
                                ))}
                                {results.errors.length > 5 && (
                                    <li>…and {results.errors.length - 5} more</li>
                                )}
                            </ul>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export { ImportProgressChip };
